/**
 * Native (Capacitor) app helpers — the same build runs as the website and inside the Android / iOS
 * apps. Everything here is a no-op on the web.
 *
 * `window.Capacitor` is injected by the native bridge before any page script runs, so these checks
 * are safe at module-evaluation time (the stock microfrontends use the same global check).
 */

export const isNativeApp = () => {
  try {
    return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
  } catch (e) {
    return false;
  }
};

/** 'android' | 'ios' | 'web' */
export const nativePlatform = () => {
  try {
    return (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web';
  } catch (e) {
    return 'web';
  }
};

/**
 * The tenant ("workspace") the app works for. On the web it comes from the hostname
 * (`<tenant>.admin.<domain>`); in the native app the page is served from `localhost`, so the user
 * picks it once and it is kept in localStorage — shared with the stock microfrontends, which run in
 * the same window and read the same key.
 */
export const TENANT_STORAGE_KEY = 'cloudix:incoming-origin';

export const getStoredTenant = () => {
  try {
    return (localStorage.getItem(TENANT_STORAGE_KEY) || '').trim();
  } catch (e) {
    return '';
  }
};

export const setStoredTenant = (tenant) => {
  try {
    localStorage.setItem(TENANT_STORAGE_KEY, tenant);
  } catch (e) {
    // storage unavailable — the workspace screen will simply show again
  }
};

export const clearStoredTenant = () => {
  try {
    localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * What the user typed → the tenant key. Accepts the bare key ("delivery") or the admin address they
 * know from the browser ("delivery.admin.gain-intel.com", "https://delivery.admin.gain-intel.com/admin").
 */
export const normalizeTenant = (input) => {
  let value = String(input || '').trim().toLowerCase();
  value = value.replace(/^[a-z]+:\/\//, '').split(/[/?#]/)[0];
  const parts = value.split('.').filter(Boolean);
  const adminIndex = parts.indexOf('admin');
  const key = adminIndex === 1 ? parts[0] : parts[0] || '';
  return /^[a-z0-9][a-z0-9-]{0,62}$/.test(key) ? key : '';
};
